import { asImageSrc, asLink, asText, isFilled } from "@prismicio/helpers"
import {
    ColorField,
    type ImageField,
    type LinkField,
    NumberField,
    RichTextField,
    SelectField,
    TitleField,
} from "@prismicio/types"
import classNames from "classnames"
import { FC, useState } from "react"

import { RichTextProps } from "../cms"
import { Link, NextImage } from "../components"
import { CTABase } from "./CTA"

interface SmallBannerProps {
    sizes: string
    text?: RichTextField
    textColor?: ColorField
    image: ImageField
    textTopPosition?: NumberField
    textAlignment?: SelectField<"center" | "left" | "right">
    buttonText?: RichTextField | TitleField | null | undefined
    buttonBgColor?: string
    buttonTextColor?: string
    buttonTopPosition?: number
    colorHover?: string
    bgColorHover?: string
    borderWidth?: number
    borderColor?: string
    fontSizeMobile?: string
    RichText?: FC<RichTextProps>
    CTA: typeof CTABase
    link: LinkField
    lcp: boolean
}

export const BannerSlot = ({
    text,
    textAlignment,
    textColor,
    textTopPosition,
    buttonText,
    buttonTopPosition,
    buttonBgColor,
    buttonTextColor,
    colorHover,
    bgColorHover,
    borderWidth,
    borderColor,
    fontSizeMobile,
    sizes,
    RichText,
    CTA,
    image,
    link,
    lcp,
}: SmallBannerProps) => {
    const showButton = !!buttonTopPosition && asText(buttonText)
    const [loaded, setLoaded] = useState(false)
    return (
        <div className="relative min-h-25 bg-contain text-center">
            <NextImage
                width={image.dimensions?.width || 0}
                height={image.dimensions?.height || 0}
                className={classNames("img-fluid w-full", loaded ? "bg-transparent" : "bg-gray-light")}
                alt={image.alt || ""}
                sizes={sizes}
                src={asImageSrc(image) || ""}
                lcp={lcp}
                onLoad={() => setLoaded(true)}
            />
            {text && isFilled.richText(text) && RichText && (
                <div
                    className={classNames("absolute left-1/2 flex w-[90%] -translate-x-1/2 flex-col", {
                        "items-center text-center": textAlignment === "center" || !textAlignment,
                        "items-start text-left": textAlignment === "left",
                        "items-end text-right": textAlignment === "right",
                    })}
                    style={{ color: textColor || undefined, top: `${textTopPosition || 50}%` }}>
                    <RichText field={text} />
                </div>
            )}
            {showButton && (
                <div
                    style={{ top: `${buttonTopPosition}%` }}
                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap">
                    <CTA
                        color={buttonTextColor || ""}
                        bgColor={buttonBgColor || ""}
                        colorHover={colorHover || ""}
                        bgColorHover={bgColorHover || ""}
                        borderWidth={borderWidth || 0}
                        borderColor={borderColor || ""}
                        fontSizeMobile={fontSizeMobile || ""}>
                        {asText(buttonText)}
                    </CTA>
                </div>
            )}
            {isFilled.link(link) && (
                <Link
                    href={asLink(link)!}
                    className="absolute inset-0"
                    aria-label={asText(buttonText) || image.alt || "banner"}
                />
            )}
        </div>
    )
}
